import { type HTMLAttributes, memo, type ReactNode } from 'react';

import Bullseye from '@/assets/icons/bullseye.svg?react';
import Collision from '@/assets/icons/collision.svg?react';
import SkullAndCrossbones from '@/assets/icons/skull-and-crossbones.svg?react';
import Trophy from '@/assets/icons/trophy.svg?react';
import { Card } from '@/shared/components/ui/card';

const StatItem = ({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: string;
  label: string;
}) => (
  <div className="flex items-center gap-1">
    <div className="text-cold-300 mb-0.5">{icon}</div>
    <div className="flex items-center gap-1">
      <p className="text-cold-50 text-lg leading-5 font-black">{value}</p>
      <p className="text-cold-300 text-xs leading-none font-medium">{label}</p>
    </div>
  </div>
);

export interface ProfileDetailsGameplaySkillOverviewProps extends HTMLAttributes<HTMLDivElement> {
  damagePerRound: number;
  kdRatio: number;
  headshotPercentage: number; // 0-1 (e.g., 0.33 for 33%)
  winPercentage: number; // 0-1 (e.g., 0.68 for 68%)
}

export const ProfileDetailsGameplaySkillOverview = memo(
  function ProfileDetailsGameplaySkillOverview(
    props: ProfileDetailsGameplaySkillOverviewProps,
  ) {
    const {
      damagePerRound,
      kdRatio,
      headshotPercentage,
      winPercentage,
      ...rest
    } = props;

    return (
      <Card variant="data" {...rest}>
        <p className="text-sm leading-5 font-bold">Skill overview</p>
        <div className="grid grid-cols-2 gap-x-2 gap-y-3">
          <StatItem
            icon={<Bullseye className="size-5" />}
            value={damagePerRound.toFixed(1)}
            label="D/round"
          />
          <StatItem
            icon={<SkullAndCrossbones className="size-5" />}
            value={kdRatio.toFixed(2)}
            label="K/D ratio"
          />
          <StatItem
            icon={<Collision className="size-5" />}
            value={`${Math.round(headshotPercentage * 100).toString()}%`}
            label="Headshot"
          />
          <StatItem
            icon={<Trophy className="size-5" />}
            value={`${Math.round(winPercentage * 100).toString()}%`}
            label="Win"
          />
        </div>
      </Card>
    );
  },
);
